import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import type { StreamingManualConfig, StreamingManualItem } from '@news-cms/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface Props {
  config: StreamingManualConfig;
  maxCount: number;
  onChange: (next: StreamingManualConfig) => void;
}

const EMPTY_ITEM: StreamingManualItem = { url: '' };

/**
 * Streaming items carry only a URL. No picker dialog (editor-authored,
 * class 2). Public renderer is responsible for detecting the stream type
 * and embedding (HLS / MPD / YouTube-live etc.) — admin just supplies the
 * URL. Simpler than ConfigEditorUrlManual since there's no title/thumb/
 * summary/embed to fill in.
 */
export function ConfigEditorStreamingManual({ config, maxCount, onChange }: Props) {
  const update = (idx: number, patch: Partial<StreamingManualItem>) => {
    const items = config.items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ ...config, items });
  };
  const move = (idx: number, dir: -1 | 1) => {
    const items = [...config.items];
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target]!, items[idx]!];
    onChange({ ...config, items });
  };
  const remove = (idx: number) =>
    onChange({ ...config, items: config.items.filter((_, i) => i !== idx) });
  const add = () => {
    if (config.items.length >= maxCount) return;
    onChange({ ...config, items: [...config.items, { ...EMPTY_ITEM }] });
  };

  const remaining = maxCount - config.items.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          {config.items.length} / {maxCount} stream
        </div>
        <Button type="button" variant="default" size="sm" disabled={remaining <= 0} onClick={add}>
          <Plus className="mr-1 h-4 w-4" /> เพิ่ม Stream
        </Button>
      </div>

      {config.items.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400 dark:border-slate-700">
          ยังไม่มี Stream — กดปุ่ม "เพิ่ม Stream" เพื่อเริ่ม
        </div>
      ) : (
        <ul className="space-y-2">
          {config.items.map((it, idx) => (
            <li
              key={idx}
              className="flex items-end gap-2 rounded-md border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
            >
              <span className="mb-2 w-6 text-center text-xs tabular-nums text-slate-400">
                #{idx + 1}
              </span>
              <div className="min-w-0 flex-1 space-y-1.5">
                <Label htmlFor={`stream-url-${idx}`}>Stream URL *</Label>
                <Input
                  id={`stream-url-${idx}`}
                  value={it.url}
                  onChange={(e) => update(idx, { url: e.target.value })}
                  placeholder="https://... (m3u8 / mpd / youtube live URL)"
                  spellCheck={false}
                  autoComplete="off"
                />
              </div>
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={idx === 0}
                  onClick={() => move(idx, -1)}
                  title="เลื่อนขึ้น"
                >
                  <ChevronUp className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={idx === config.items.length - 1}
                  onClick={() => move(idx, 1)}
                  title="เลื่อนลง"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => remove(idx)}
                  title="ลบ"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
